// firebase.js
import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence, browserSessionPersistence, inMemoryPersistence, indexedDBLocalPersistence } from 'firebase/auth';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import { getStorage } from 'firebase/storage'; 
import { GoogleAuthProvider, signInWithCredential } from 'firebase/auth';

// Import the config from the config file
import { firebaseConfig } from './config/firebaseConfig';

let app;
let auth = null;
let db = null;
let storage = null;

try {
  app = initializeApp(firebaseConfig);
  
  // Initialize Auth with persistence settings
  auth = getAuth(app);

  // Set persistence to LOCAL for better reliability
  setPersistence(auth, browserLocalPersistence)
    .catch((error) => {
      console.warn('Failed to set persistence:', error);
    });

  // Initialize Firestore
  db = getFirestore(app);
  
  // Initialize Storage
  storage = getStorage(app);
  
  // Add event listener for auth state changes
  auth.onAuthStateChanged((user) => {
    // State change handled silently
  });
  
  // Enhanced persistence handling for mobile devices
  (async () => {
    try {
      // Check if we're on a mobile device
      const userAgent = navigator.userAgent;
      const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);
      
      if (isMobile) {
        // For mobile devices, try multiple persistence methods
        try {
          // First try browserLocalPersistence
          await setPersistence(auth, browserLocalPersistence);
        } catch (error) {
          console.warn('Failed to set browserLocalPersistence:', error);
          
          // If that fails, try session persistence
          try {
            await setPersistence(auth, browserSessionPersistence);
          } catch (sessionError) {
            console.warn('Failed to set browserSessionPersistence:', sessionError);
            
            // As a last resort, use inMemoryPersistence
            await setPersistence(auth, inMemoryPersistence);
          }
        }
      } else {
        // For desktop, use browserLocalPersistence
        await setPersistence(auth, browserLocalPersistence);
      }
    } catch (error) {
      console.warn('Failed to set persistence:', error);
    }
  })();

  // Force a token refresh on init to ensure we have the latest auth state
  auth.onAuthStateChanged(async (user) => {
    if (user) {
      try {
        // Force token refresh to ensure auth state is current
        await user.getIdToken(true);
        
        // For mobile devices, store the token in localStorage as backup
        if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
          try {
            const token = await user.getIdToken();
            localStorage.setItem('authToken', token);
            localStorage.setItem('authTokenTimestamp', Date.now().toString());
          } catch (e) {
            console.warn('Failed to store auth token:', e);
          }
        }
      } catch (tokenError) {
        console.warn('Error refreshing token:', tokenError);
      }
    }
  });

  // Handle OAuth redirect for mobile devices
  if (typeof window !== 'undefined') {
    // Check if we're coming back from OAuth
    const hash = window.location.hash;
    if (hash && hash.includes('id_token=')) {
      try {
        // Parse the ID token from the URL
        const idToken = hash.split('id_token=')[1].split('&')[0];
        
        // Get stored nonce for verification
        const storedNonce = sessionStorage.getItem('auth_nonce');
        
        // TODO: Properly verify the nonce in the token
        // For now, we're just proceeding with the sign-in
        
        // Sign in with the ID token
        const credential = GoogleAuthProvider.credential(idToken);
        await signInWithCredential(auth, credential);
        
        // Clear the URL hash and stored nonce
        window.location.hash = '';
        sessionStorage.removeItem('auth_nonce');
        
        // Notify the parent window
        if (window.opener) {
          try {
            window.opener.postMessage('auth-success', '*'); // Use * for origin to avoid CORS issues
          } catch (postError) {
            if (window.opener.location.origin === window.location.origin) {
              window.opener.location.href = '/';
            }
          }
        } else {
          // If no opener, we might be in a redirect flow, so redirect to the app
          window.location.href = '/';
        }
      } catch (error) {
        console.error('Error handling OAuth redirect:', error);
        
        // Clear stored nonce
        sessionStorage.removeItem('auth_nonce');
        
        if (window.opener) {
          try {
            window.opener.postMessage('auth-error', '*'); // Use * for origin to avoid CORS issues
          } catch (postError) {
          }
        } else {
          // If no opener, redirect to login with error
          window.location.href = '/login?error=auth-failed';
        }
      }
    }
  }

  // Configure CSP and Trusted Types for mobile browsers
  if (typeof window !== 'undefined') {
    // Define the desired permissive CSP content
    const cspContent = "default-src * 'unsafe-inline' 'unsafe-eval' data: blob:; " + 
                       "script-src * 'unsafe-inline' 'unsafe-eval' blob:; " + 
                       "script-src-elem * 'unsafe-inline' blob:; " + 
                       "style-src * 'unsafe-inline'; " + 
                       "img-src * data: blob:; " + 
                       "font-src * data:; " + 
                       "connect-src * data:; " + 
                       "frame-src * data:;";

    // Find existing CSP meta tag or create a new one
    let meta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.httpEquiv = "Content-Security-Policy";
      document.head.appendChild(meta);
    }
    // Always set the content to the desired permissive policy
    meta.content = cspContent;

    // Configure Trusted Types if supported
    if (window.trustedTypes && window.trustedTypes.createPolicy) {
      try {
        window.trustedTypes.createPolicy('default', {
          createHTML: (string) => string,
          createScriptURL: (string) => string,
          createScript: (string) => string,
        });
      } catch (e) {
      }
    }
  }

  // Update Google Sign-In configuration
  const googleProvider = new GoogleAuthProvider();
  googleProvider.setCustomParameters({
    'access_type': 'offline',
    'prompt': 'consent',
    'include_granted_scopes': true
  });

  // Add CORS headers to Google Sign-In requests
  const addCorsHeaders = (url) => {
    const headers = new Headers();
    headers.append('Access-Control-Allow-Origin', '*');
    headers.append('Access-Control-Allow-Methods', 'GET, POST, OPTIONS');
    headers.append('Access-Control-Allow-Headers', 'Content-Type');
    return { headers };
  };

  // Update loadGoogleIdentityServices function
  const loadGoogleIdentityServices = async () => {
    try {
      // Check if script already exists
      if (document.getElementById('google-identity-services')) {
        return;
      }

      // Create script element with CORS headers
      const script = document.createElement('script');
      script.id = 'google-identity-services';
      script.async = true;
      script.defer = true;

      // Add CORS headers
      const corsHeaders = addCorsHeaders('https://accounts.google.com/gsi/client');
      Object.entries(corsHeaders.headers).forEach(([key, value]) => {
        script.setAttribute(key, value);
      });

      // Set script source
      script.src = 'https://accounts.google.com/gsi/client';

      // Add to document
      document.body.appendChild(script);

      // Return promise that resolves when script loads
      return new Promise((resolve, reject) => {
        script.onload = resolve;
        script.onerror = reject;
      });
    } catch (error) {
      console.warn('Error loading Google Identity Services:', error);
      throw error;
    }
  };

  // Handle OAuth sign-in for mobile when sessionStorage is inaccessible
  if (typeof window !== 'undefined') {
    // Check if we're in a partitioned storage environment
    const isStoragePartitioned = () => {
      try {
        const testKey = 'firebase-storage-test';
        localStorage.setItem(testKey, '1');
        const value = localStorage.getItem(testKey);
        localStorage.removeItem(testKey);
        return value !== '1';
      } catch (e) {
        console.warn('Storage test failed:', e);
        return true;
      }
    };
    
    // If we detect partitioned storage or missing initial state
    if (isStoragePartitioned() || window.location.search.includes('missing_initial_state')) {
      // Create global handler to work around storage issues
      window.handleFirebaseAuth = {
        state: new Map(),
        pendingRedirect: false,
        
        // Functions to interact with auth state
        getState: (key) => window.handleFirebaseAuth.state.get(key),
        setState: (key, value) => window.handleFirebaseAuth.state.set(key, value),
        clearState: () => window.handleFirebaseAuth.state.clear(),
        
        // Mark that a redirect is in progress
        startRedirect: () => {
          window.handleFirebaseAuth.pendingRedirect = true;
        }
      };
    }
  }

} catch (error) {
  // Report error for debugging
  const errorElement = document.createElement('div');
  errorElement.style.display = 'none';
  errorElement.setAttribute('id', 'firebase-init-error');
  errorElement.setAttribute('data-error', error.message);
  document.body.appendChild(errorElement);
}

export { auth, db, storage };

/**
 * Creates an admin user in the database
 * Call this function manually in development to set up an admin user
 * @param {string} userId - The Firebase user ID to grant admin privileges to
 * @param {string} email - The admin user's email
 */
export const createAdminUser = async (userId, email) => {
  try {
    if (!userId || !email || !db) {
      return { success: false, error: 'Required data missing or Firebase initialization failed' };
    }
    
    await setDoc(doc(db, 'admins', userId), {
      userId,
      email,
      isAdmin: true,
      createdAt: new Date().toISOString(),
    });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export default app;
